import type { StateCreator } from 'zustand/vanilla';

import type { SharingProfileStateType } from '@/features/sharing-profile/types/sharing-profile.interface';

import type { SharingStore } from '../../store';
import type { ProfileDraft } from './initial-state';

/** Empty draft shape used when starting a new profile (create flow) or after a reset. */
function buildEmptyProfileDraft(spaceId = ''): ProfileDraft {
  return {
    id: '',
    name: '',
    description: '',
    space_id: spaceId,
    resources: { datasets: {}, collections: {}, documents: {} },
  };
}

export interface SharingProfilesAction {
  /** Replace the full profiles list (e.g. after a fetch for the current space). */
  setProfiles: (profiles: SharingProfileStateType[]) => void;
  /** Append a newly created profile to the list. */
  addProfile: (profile: SharingProfileStateType) => void;
  /** Merge a partial update into the profile matching `id` within the list. No-op if not found. */
  updateProfile: (id: string, partial: Partial<SharingProfileStateType>) => void;
  /** Remove a profile from the list by id. Also clears the selection if it pointed at the removed profile. */
  removeProfile: (id: string) => void;
  /** Mark a profile as selected by id. */
  selectProfile: (id: string) => void;
  /** Clear the current profile selection. */
  clearSelectedProfile: () => void;
  /** Replace the entire edit-form draft (e.g. when opening the stepper for create or edit). */
  setProfileDraft: (draft: ProfileDraft) => void;
  /** Merge a partial update into the current edit-form draft. No-op if there is no active draft. */
  updateProfileDraft: (partial: Partial<ProfileDraft>) => void;
  /** Reset the edit-form draft back to an empty profile, optionally pre-filled with a space id. */
  resetProfileDraft: (spaceId?: string) => void;
  /** Set the profiles loading state. */
  setProfilesLoading: (isLoading: boolean) => void;
  /** Set the profiles error message. Setting an error also clears the loading state. */
  setProfilesError: (error: string | undefined) => void;
}

export const createProfilesSlice: StateCreator<SharingStore, [['zustand/devtools', never]], [], SharingProfilesAction> = (set, get) => ({
  setProfiles: (profiles) => {
    set({ profiles });
  },

  addProfile: (profile) => {
    set({ profiles: [...get().profiles, profile] });
  },

  updateProfile: (id, partial) => {
    set({
      profiles: get().profiles.map((profile) => (profile.id === id ? { ...profile, ...partial } : profile)),
    });
  },

  removeProfile: (id) => {
    const { profiles, selectedProfileId } = get();
    set({
      profiles: profiles.filter((profile) => profile.id !== id),
      selectedProfileId: selectedProfileId === id ? undefined : selectedProfileId,
    });
  },

  selectProfile: (id) => {
    set({ selectedProfileId: id });
  },

  clearSelectedProfile: () => {
    set({ selectedProfileId: undefined });
  },

  setProfileDraft: (draft) => {
    set({ profileDraft: draft });
  },

  updateProfileDraft: (partial) => {
    const { profileDraft } = get();
    if (!profileDraft) return;
    set({ profileDraft: { ...profileDraft, ...partial } });
  },

  resetProfileDraft: (spaceId) => {
    set({ profileDraft: buildEmptyProfileDraft(spaceId) });
  },

  setProfilesLoading: (isLoading) => {
    set({ isLoading });
  },

  setProfilesError: (error) => {
    set({ error, isLoading: false });
  },
});
